<!-- Activity Reports Component -->
<div class="activity-reports-container">
  <div class="card">
    <div class="card-header bg-primary text-white">
      <h4 class="card-title mb-0">
        <mat-icon class="me-2">assessment</mat-icon>
        Rapoarte și Analize Activități
      </h4>
    </div>
    
    <div class="card-body">
      <!-- Filter Section -->
      <div class="row mb-4">
        <div class="col-12">
          <form [formGroup]="filterForm" (ngSubmit)="applyFilters()" class="row g-3">
            <!-- Period Selection -->
            <div class="col-md-3">
              <label for="period" class="form-label">Perioadă</label>
              <select 
                id="period" 
                class="form-select" 
                formControlName="period"
                [class.is-invalid]="filterForm.get('period')?.invalid && filterForm.get('period')?.touched">
                <option *ngFor="let option of periodOptions" [value]="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            
            <!-- Custom Date Range (shown only when period is 'custom') -->
            <div class="col-md-2" *ngIf="filterForm.get('period')?.value === 'custom'">
              <label for="startDate" class="form-label">Data început</label>
              <input 
                type="date" 
                id="startDate" 
                class="form-control" 
                formControlName="startDate"
                [class.is-invalid]="filterForm.get('startDate')?.invalid && filterForm.get('startDate')?.touched">
            </div>
            
            <div class="col-md-2" *ngIf="filterForm.get('period')?.value === 'custom'">
              <label for="endDate" class="form-label">Data sfârșit</label>
              <input 
                type="date" 
                id="endDate" 
                class="form-control" 
                formControlName="endDate"
                [class.is-invalid]="filterForm.get('endDate')?.invalid && filterForm.get('endDate')?.touched">
            </div>
            
            <!-- Activity Type Filter -->
            <div class="col-md-2">
              <label for="activityType" class="form-label">Tip activitate</label>
              <select id="activityType" class="form-select" formControlName="activityType">
                <option value="all">Toate tipurile</option>
                <!-- Activity types will be loaded dynamically -->
              </select>
            </div>
            
            <!-- Status Filter -->
            <div class="col-md-2">
              <label for="status" class="form-label">Status</label>
              <select id="status" class="form-select" formControlName="status">
                <option value="all">Toate statusurile</option>
                <option value="scheduled">Programate</option>
                <option value="completed">Completate</option>
                <option value="cancelled">Anulate</option>
                <option value="rescheduled">Reprogramate</option>
                <option value="no_show">Absențe</option>
              </select>
            </div>
            
            <!-- Actions -->
            <div class="col-md-3 d-flex align-items-end">
              <div class="form-check me-3">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="compareWithPrevious" 
                  formControlName="compareWithPrevious">
                <label class="form-check-label small" for="compareWithPrevious">
                  Compară cu perioada anterioară
                </label>
              </div>
              <button 
                type="submit" 
                class="btn btn-primary me-2"
                [disabled]="isLoading">
                <mat-icon class="me-1">search</mat-icon>
                Aplică filtre
              </button>
              <button 
                type="button" 
                class="btn btn-outline-secondary me-2"
                (click)="resetFilters()"
                [disabled]="isLoading">
                <mat-icon>refresh</mat-icon>
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <!-- Loading State -->
      <div *ngIf="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Se încarcă...</span>
        </div>
        <p class="mt-2 text-muted">Se încarcă datele...</p>
      </div>
      
      <!-- No Data State -->
      <div *ngIf="!isLoading && !hasData" class="text-center py-5">
        <mat-icon class="display-1 text-muted">trending_neutral</mat-icon>
        <h5 class="mt-3 text-muted">Nu există date pentru perioada selectată</h5>
        <p class="text-muted">Încercați să modificați filtrele sau să selectați o altă perioadă.</p>
      </div>
      
      <!-- Main Content -->
      <div *ngIf="!isLoading && hasData && currentMetrics">
        <!-- Summary Cards -->
        <div class="row mb-4">
          <!-- Total Activities -->
          <div class="col-md-3">
            <div class="card bg-primary text-white h-100">
              <div class="card-body d-flex align-items-center">
                <div>
                  <h6 class="card-title">Total Activități</h6>
                  <h2 class="mb-0">{{ currentMetrics.report.totalActivities }}</h2>
                  <small *ngIf="hasComparisonData" class="opacity-75">
                    <mat-icon class="small me-1" 
                      [ngClass]="getChangeColor(currentMetrics.report.totalActivities, comparisonMetrics!.report.totalActivities)">
                      {{ getChangeIcon(currentMetrics.report.totalActivities, comparisonMetrics!.report.totalActivities) }}
                    </mat-icon>
                    {{ getPercentageChange(currentMetrics.report.totalActivities, comparisonMetrics!.report.totalActivities) }}%
                  </small>
                </div>
                <mat-icon class="ms-auto display-6 opacity-50">event</mat-icon>
              </div>
            </div>
          </div>
          
          <!-- Completed Activities -->
          <div class="col-md-3">
            <div class="card bg-success text-white h-100">
              <div class="card-body d-flex align-items-center">
                <div>
                  <h6 class="card-title">Completate</h6>
                  <h2 class="mb-0">{{ currentMetrics.report.completedActivities }}</h2>
                  <small *ngIf="hasComparisonData" class="opacity-75">
                    <mat-icon class="small me-1">
                      {{ getChangeIcon(currentMetrics.report.completedActivities, comparisonMetrics!.report.completedActivities) }}
                    </mat-icon>
                    {{ getPercentageChange(currentMetrics.report.completedActivities, comparisonMetrics!.report.completedActivities) }}%
                  </small>
                </div>
                <mat-icon class="ms-auto display-6 opacity-50">check_circle</mat-icon>
              </div>
            </div>
          </div>
          
          <!-- Completion Rate -->
          <div class="col-md-3">
            <div class="card bg-info text-white h-100">
              <div class="card-body d-flex align-items-center">
                <div>
                  <h6 class="card-title">Rata Completare</h6>
                  <h2 class="mb-0">{{ currentMetrics.report.completionRate }}%</h2>
                  <small *ngIf="hasComparisonData" class="opacity-75">
                    <mat-icon class="small me-1">
                      {{ getChangeIcon(currentMetrics.report.completionRate, comparisonMetrics!.report.completionRate) }}
                    </mat-icon>
                    {{ getPercentageChange(currentMetrics.report.completionRate, comparisonMetrics!.report.completionRate) }}%
                  </small>
                </div>
                <mat-icon class="ms-auto display-6 opacity-50">trending_up</mat-icon>
              </div>
            </div>
          </div>
          
          <!-- Total Hours -->
          <div class="col-md-3">
            <div class="card bg-warning text-white h-100">
              <div class="card-body d-flex align-items-center">
                <div>
                  <h6 class="card-title">Total Ore</h6>
                  <h2 class="mb-0">{{ currentMetrics.report.totalHours }}h</h2>
                  <small *ngIf="hasComparisonData" class="opacity-75">
                    <mat-icon class="small me-1">
                      {{ getChangeIcon(currentMetrics.report.totalHours, comparisonMetrics!.report.totalHours) }}
                    </mat-icon>
                    {{ getPercentageChange(currentMetrics.report.totalHours, comparisonMetrics!.report.totalHours) }}%
                  </small>
                </div>
                <mat-icon class="ms-auto display-6 opacity-50">schedule</mat-icon>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Detailed Stats -->
        <div class="row mb-4">
          <div class="col-md-8">
            <!-- Charts Section -->
            <div class="card h-100">
              <div class="card-header">
                <h6 class="card-title mb-0">Analize Vizuale</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <!-- Status Distribution Chart -->
                  <div class="col-lg-6 mb-3">
                    <h6 class="mb-3">Distribuție pe Status</h6>
                    <div style="height: 250px;">
                      <!-- Chart placeholder - requires chart library integration -->
                      <div class="d-flex justify-content-center align-items-center h-100 bg-light rounded">
                        <div class="text-center">
                          <mat-icon class="display-4 text-muted">donut_large</mat-icon>
                          <p class="text-muted">Grafic distribuție status</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Activity Type Chart -->
                  <div class="col-lg-6 mb-3">
                    <h6 class="mb-3">Tipuri de Activități</h6>
                    <div style="height: 250px;">
                      <!-- Chart placeholder -->
                      <div class="d-flex justify-content-center align-items-center h-100 bg-light rounded">
                        <div class="text-center">
                          <mat-icon class="display-4 text-muted">pie_chart</mat-icon>
                          <p class="text-muted">Grafic tipuri activități</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Monthly Trend Chart -->
                <div class="row" *ngIf="currentMetrics.report.monthlyStats.length > 1">
                  <div class="col-12">
                    <h6 class="mb-3">Tendință Lunară</h6>
                    <div style="height: 300px;">
                      <!-- Chart placeholder -->
                      <div class="d-flex justify-content-center align-items-center h-100 bg-light rounded">
                        <div class="text-center">
                          <mat-icon class="display-4 text-muted">show_chart</mat-icon>
                          <p class="text-muted">Grafic tendință lunară</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-4">
            <!-- Additional Stats -->
            <div class="card h-100">
              <div class="card-header">
                <h6 class="card-title mb-0">Statistici Detaliate</h6>
              </div>
              <div class="card-body">
                <!-- Status Breakdown -->
                <div class="mb-4">
                  <h6 class="text-muted">Breakdown pe Status</h6>
                  <div class="list-group list-group-flush">
                    <div class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-success">
                        <mat-icon class="me-2 small">check_circle</mat-icon>
                        Completate
                      </span>
                      <span class="badge bg-success rounded-pill">{{ currentMetrics.report.completedActivities }}</span>
                    </div>
                    <div class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-primary">
                        <mat-icon class="me-2 small">schedule</mat-icon>
                        Programate
                      </span>
                      <span class="badge bg-primary rounded-pill">{{ currentMetrics.report.pendingActivities }}</span>
                    </div>
                    <div class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-warning">
                        <mat-icon class="me-2 small">event_repeat</mat-icon>
                        Reprogramate
                      </span>
                      <span class="badge bg-warning rounded-pill">{{ currentMetrics.report.rescheduledActivities }}</span>
                    </div>
                    <div class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-danger">
                        <mat-icon class="me-2 small">cancel</mat-icon>
                        Anulate
                      </span>
                      <span class="badge bg-danger rounded-pill">{{ currentMetrics.report.cancelledActivities }}</span>
                    </div>
                    <div class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-secondary">
                        <mat-icon class="me-2 small">person_off</mat-icon>
                        Absențe
                      </span>
                      <span class="badge bg-secondary rounded-pill">{{ currentMetrics.report.noShowActivities }}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Performance Metrics -->
                <div class="mb-4">
                  <h6 class="text-muted">Metrici Performanță</h6>
                  <div class="row g-2">
                    <div class="col-12">
                      <div class="bg-light p-2 rounded small">
                        <strong>Durata medie:</strong> {{ currentMetrics.report.averageDuration }}h
                      </div>
                    </div>
                    <div class="col-12">
                      <div class="bg-light p-2 rounded small">
                        <strong>Rata la timp:</strong> {{ currentMetrics.report.onTimeRate }}%
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Activity Types -->
                <div class="mb-4" *ngIf="currentMetrics.report.activityTypeBreakdown">
                  <h6 class="text-muted">Top Tipuri Activități</h6>
                  <div class="list-group list-group-flush">
                    <div 
                      *ngFor="let type of currentMetrics.report.activityTypeBreakdown | keyvalue" 
                      class="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span class="text-truncate me-2">{{ type.key }}</span>
                      <span class="badge bg-secondary rounded-pill">{{ type.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Comparison Section -->
        <div class="row mb-4" *ngIf="hasComparisonData && comparisonMetrics">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="card-title mb-0">Comparație cu Perioada Anterioară</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th class="text-center">{{ currentMetrics.period }}</th>
                        <th class="text-center">{{ comparisonMetrics.period }}</th>
                        <th class="text-center">Diferență</th>
                        <th class="text-center">Schimbare (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Total Activități</td>
                        <td class="text-center">{{ currentMetrics.report.totalActivities }}</td>
                        <td class="text-center">{{ comparisonMetrics.report.totalActivities }}</td>
                        <td class="text-center">
                          {{ currentMetrics.report.totalActivities - comparisonMetrics.report.totalActivities }}
                        </td>
                        <td class="text-center" 
                          [ngClass]="getChangeColor(currentMetrics.report.totalActivities, comparisonMetrics.report.totalActivities)">
                          {{ getPercentageChange(currentMetrics.report.totalActivities, comparisonMetrics.report.totalActivities) }}%
                        </td>
                      </tr>
                      <tr>
                        <td>Activități Completate</td>
                        <td class="text-center">{{ currentMetrics.report.completedActivities }}</td>
                        <td class="text-center">{{ comparisonMetrics.report.completedActivities }}</td>
                        <td class="text-center">
                          {{ currentMetrics.report.completedActivities - comparisonMetrics.report.completedActivities }}
                        </td>
                        <td class="text-center" 
                          [ngClass]="getChangeColor(currentMetrics.report.completedActivities, comparisonMetrics.report.completedActivities)">
                          {{ getPercentageChange(currentMetrics.report.completedActivities, comparisonMetrics.report.completedActivities) }}%
                        </td>
                      </tr>
                      <tr>
                        <td>Rata Completare</td>
                        <td class="text-center">{{ currentMetrics.report.completionRate }}%</td>
                        <td class="text-center">{{ comparisonMetrics.report.completionRate }}%</td>
                        <td class="text-center">
                          {{ currentMetrics.report.completionRate - comparisonMetrics.report.completionRate | number:'1.1-1' }}%
                        </td>
                        <td class="text-center" 
                          [ngClass]="getChangeColor(currentMetrics.report.completionRate, comparisonMetrics.report.completionRate)">
                          {{ getPercentageChange(currentMetrics.report.completionRate, comparisonMetrics.report.completionRate) }}%
                        </td>
                      </tr>
                      <tr>
                        <td>Total Ore</td>
                        <td class="text-center">{{ currentMetrics.report.totalHours }}h</td>
                        <td class="text-center">{{ comparisonMetrics.report.totalHours }}h</td>
                        <td class="text-center">
                          {{ currentMetrics.report.totalHours - comparisonMetrics.report.totalHours | number:'1.1-1' }}h
                        </td>
                        <td class="text-center" 
                          [ngClass]="getChangeColor(currentMetrics.report.totalHours, comparisonMetrics.report.totalHours)">
                          {{ getPercentageChange(currentMetrics.report.totalHours, comparisonMetrics.report.totalHours) }}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Export Section -->
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h6 class="card-title mb-0">Export Raport</h6>
              </div>
              <div class="card-body">
                <form [formGroup]="exportForm" class="row g-3">
                  <div class="col-md-3">
                    <label for="reportType" class="form-label">Tip raport</label>
                    <select id="reportType" class="form-select" formControlName="reportType">
                      <option *ngFor="let option of reportTypeOptions" [value]="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  
                  <div class="col-md-2">
                    <label for="format" class="form-label">Format</label>
                    <select id="format" class="form-select" formControlName="format">
                      <option *ngFor="let option of exportFormatOptions" [value]="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  
                  <div class="col-md-3">
                    <label class="form-label">Opțiuni</label>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="includeCharts" formControlName="includeCharts">
                      <label class="form-check-label" for="includeCharts">
                        Include grafice
                      </label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="includeDetails" formControlName="includeDetails">
                      <label class="form-check-label" for="includeDetails">
                        Include detalii complete
                      </label>
                    </div>
                  </div>
                  
                  <div class="col-md-4 d-flex align-items-end">
                    <button 
                      type="button" 
                      class="btn btn-success me-2"
                      (click)="exportReport()"
                      [disabled]="isExporting || !exportForm.valid">
                      <mat-icon class="me-1">download</mat-icon>
                      {{ isExporting ? 'Se exportă...' : 'Exportă raport' }}
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-outline-primary"
                      (click)="refreshData()"
                      [disabled]="isLoading">
                      <mat-icon class="me-1">refresh</mat-icon>
                      Reîmprospătează
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>